import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import BookRecovery from '../views/Book/BookRecovery';
import { AppointmentRouteLoading } from '../views/AppointmentAction/AppointmentLoading';
import Book, { STEPS } from '../views/Book';
import Landing from '../views/Landing';
import NotFound from '../views/NotFound';
import Profile from '../views/Profile';
import RouteError from '../views/RouteError';

// Route table for book.uselura.com.
//
// Exported as a plain array rather than a built router so the shape can be
// tested with createMemoryRouter. index.tsx wraps it for the browser.
//
// The shape is dictated by two constraints, both recorded in
// docs/architecture/routes-book.md — which is the artifact mobile reads, and
// the reason that file exists rather than this comment being the only record:
//
//  1. A professional's booking link is a ROOT-LEVEL SLUG — book.uselura.com/<slug>.
//     The root IS that slug space, so anything else living there shadows a real
//     professional's link. It was also written against a native claim on the
//     root; September Option A makes book.* Web-owned, and the rule holds either
//     way.
//
//  2. Appointment-action links therefore live under /a/*, a namespace mobile
//     can exclude. A native exclusion can name a prefix but cannot express
//     "tokens that happen to look like slugs", so a single-segment token at the
//     root would be intercepted, fail to resolve as a slug, and tell a client
//     their valid cancellation link is dead — inside an app they were never
//     required to install. Agreed with mobile and backend 2026-08-25.
//
// Because the slug is a bare root segment, EVERY new top-level path takes a
// name out of the slug space. `index.test.tsx` fails if this list gains one, so
// adding a path is a deliberate edit with a matching backend reservation —
// see docs/planning/book-reserved-paths.md.
//
// ONE ROUTE IS LAZY: /a (rc.9 entry headroom, Web Director 2026-10-15).
// Everything a BOOKING link needs stays in the entry. A split there buys a
// round trip and a blank frame on the first paint of a booking link, which is
// a phone on a mobile network following a QR code (perf D3, 2026-10-07:
// boot is network-bound, so round trips are the lever, not chunks).
//
// The appointment page is the exception: every booking visitor downloaded it,
// and only appointment links use it. Its chunk costs those links no extra
// round trip either: router/index.tsx starts it at entry evaluation when the
// first URL is under /a, in parallel with the entry's own start-up, and
// AppointmentRouteLoading shows the page's own loading nodes meanwhile. A
// chunk that fails (a deploy in between) lands on RouteError, whose retry
// reloads the same path and with it the new index.html.
/**
 * Validates the step segment before Book sees it.
 *
 * `/x/book/nonsense` is a wrong-shaped URL, not a broken flow — rendering an
 * empty step would leave someone staring at chrome with no content and no way
 * to tell what went wrong.
 */
function BookStep() {
  const { step } = useParams<{ step: string }>();
  if (!step || !(STEPS as readonly string[]).includes(step)) return <NotFound />;
  return <Book step={step as (typeof STEPS)[number]} />;
}

/** The appointment page's chunk. Also started early by router/index.tsx. */
export const loadAppointmentAction = () => import('../views/AppointmentAction');
const appointmentAction = async () => ({ Component: (await loadAppointmentAction()).default });

/**
 * Stripe's return (payments prep): registered ONLY while PAYMENTS_ENABLED
 * (config/payments.ts), so a committed build has no such route and the
 * catch-all answers. `checkout` is in the slug registry's reserved names
 * (docs/planning/book-reserved-paths.md, group 5, accepted by Backend
 * 2026-08-23). Its own lazy chunk (`checkout-return-*`).
 */
export function checkoutReturnRoute(load: () => Promise<{ default: ComponentType }>): RouteObject {
  return { path: '/checkout/return', lazy: async () => ({ Component: (await load()).default }), errorElement: <RouteError /> };
}
// The build-time literal, not the imported constant: the import() must not
// even be scanned while the gate is closed (vite-env.d.ts).
const paymentRoutes: RouteObject[] = __LURA_PAYMENTS__ ? [checkoutReturnRoute(() => import('../views/CheckoutReturn'))] : [];

const routes: RouteObject[] = [
  { path: '/:slug/book/reset', Component: BookRecovery, errorElement: <RouteError /> },
  ...paymentRoutes,
  {
    path: '/',
    Component: Landing,
    errorElement: <RouteError />,
  },
  {
    // The WHOLE /a prefix is owned, not just /a/:token.
    //
    // Mobile excludes the prefix, so every URL under it is handed to the
    // browser — including the ones an email client mangles. `/a` and `/a/`
    // would otherwise match `/:slug` and render the professional-profile
    // shell, which at Stage 1 becomes the UNAVAILABLE message. A client with a
    // truncated management link would be told a professional does not exist:
    // the exact failure this namespace was created to avoid, relocated from
    // the app to the web.
    //
    // `a` costs nothing from the slug space — the backend grammar has a
    // three-character floor, so a one-character slug can never be issued.
    path: '/a',
    errorElement: <RouteError />,
    // Static, beside `lazy`: the fallback is needed before the chunk is.
    HydrateFallback: AppointmentRouteLoading,
    children: [
      { index: true, lazy: appointmentAction },
      { path: ':token', lazy: appointmentAction },
      { path: '*', lazy: appointmentAction },
    ],
  },
  {
    // The booking flow, NESTED under the slug — so it costs nothing from the
    // root slug space, which every top-level path spends permanently.
    //
    // The step is in the PATH and the selection is in the QUERY STRING. Back
    // and forward then work the way a phone user expects, and a refresh does
    // not cost three steps of choices. See features/booking/useFlowState.
    path: '/:slug/book/:step',
    Component: BookStep,
    errorElement: <RouteError />,
  },
  {
    // The professional's public booking page. Stage 1 fills this in.
    //
    // Profile itself rejects anything the slug grammar forbids — see its
    // comment. The route cannot do it: React Router matches path shape, not
    // content, so `/favicon.ico` and `/Dans-Grass-Cutters` both land here.
    path: '/:slug',
    Component: Profile,
    errorElement: <RouteError />,
  },
  {
    path: '*',
    Component: NotFound,
    errorElement: <RouteError />,
  },
];

export default routes;
